import { miscHelpers } from '@approvalmax/utils';

import ConditionWrapper from '../../../ConditionWrapper/ConditionWrapper';
import Tooltip from '../../../Tooltip/Tooltip';
import { useTableContext } from '../../Table.context';
import { defaultSpacing } from '../../Table.styles';
import { BaseItem } from '../../Table.types';
import { CellWarningIndicator } from '../CellWarningIndicator/CellWarningIndicator';
import { StyledCell } from './BodyCell.styles';
import { BodyCellProps } from './BodyCell.types';

export const BodyCell = <Item extends BaseItem>(props: BodyCellProps<Item>) => {
    const {
        value,
        children,
        title,
        columnDefinition,
        bordered,
        invalid,
        focus,
        cursor,
        hover,
        disabled,
        columnSpacing,
        item,
        warningLevel: propWarningLevel,
        warningMessage: propWarningMessage,
        ...otherProps
    } = props;

    const spacing = miscHelpers.spacingPropToCss(columnDefinition.spacing ?? columnSpacing ?? defaultSpacing);

    const { isLeftStickyShadow, isRightStickyShadow } = useTableContext();

    const withStickyShadow =
        (columnDefinition.sticky === 'left' && isLeftStickyShadow) ||
        (columnDefinition.sticky === 'right' && isRightStickyShadow);

    const columnWarningLevel =
        columnDefinition.warningLevel && typeof columnDefinition.warningLevel === 'function'
            ? columnDefinition.warningLevel(item)
            : columnDefinition.warningLevel;
    const warningLevel = propWarningLevel || columnWarningLevel;

    const columnWarningMessage =
        columnDefinition.warningMessage && typeof columnDefinition.warningMessage === 'function'
            ? columnDefinition.warningMessage(item)
            : columnDefinition.warningMessage;
    const warningMessage = propWarningMessage || columnWarningMessage;

    return (
        <StyledCell
            title={title}
            $spacing={spacing}
            $sticky={columnDefinition.sticky}
            $withStickyShadow={withStickyShadow}
            $bordered={bordered}
            $textAlign={columnDefinition.textAlign}
            $verticalAlign={columnDefinition.verticalAlign}
            $invalid={invalid}
            $focus={focus}
            $cursor={cursor}
            $hover={hover}
            $disabled={disabled}
            {...otherProps}
        >
            <ConditionWrapper
                condition={Boolean(warningMessage && warningLevel)}
                wrapperIf={(children) => (
                    <Tooltip display='block' placement='top' width='max-content' maxWidth={300} activator={children}>
                        {warningMessage}
                    </Tooltip>
                )}
            >
                {children || value}

                {warningLevel && <CellWarningIndicator level={warningLevel} />}
            </ConditionWrapper>
        </StyledCell>
    );
};

BodyCell.displayName = 'BodyCell';

export default BodyCell;
